<template>
  <div class="privilege-form">
    <HeadSection
      :title="isEditMode ? `Item #${privilegeToEdit?.id}` : 'Create new privilege'"
      permissionOfSection="privileges"
      :breadcrumbs="breadcrumbs"
    >
      <template #activeSwitch>
        <div class="ml-4">
          <a-switch v-model="form.isActive" />
        </div>
      </template>
      <template #subtitle>
        <div v-if="isEditMode" style="color: #8c8c8c; font-size: 14px; margin-top: 6px;">
          UUID: {{ privilegeToEdit?.uuid }}
        </div>
      </template>
      <template #actions>
        <a-col :span="14" class="text-right d-flex justify-content-end">
          <div v-if="isEditMode">
            <a-popconfirm title="Sure to delete?" placement="bottom" @confirm.stop="deletePrivilege">
              <a-button :loading="loading" type="danger"> Delete </a-button>
            </a-popconfirm>
            <a-button :loading="loading" type="primary" class="ml-4" @click="save">
              Save
            </a-button>
          </div>
          <div v-else class="pt-4">
            <a-button class="ml-4" @click="clearForm" :loading="loading">
              Clear
            </a-button>
            <a-button :loading="loading" type="primary" class="ml-4" @click="save">
              Save
            </a-button>
          </div>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer
      :loading="loading || userLoading"
      permissionOfSection="privileges"
    >
      <a-form-model :rules="rules" :model="form" ref="form">
        <PrivilegesGeneral
          ref="block-general"
          v-model="form"
          :loading="loading"
          :segments="segments"
          :screens="screens"
          @openAppModal="openAddAppModal"
          @validateField="validateFormField"
          @updatePeriods="updatePeriods"
        />
      </a-form-model>
      <a-divider class="m-0 mb-3" />
      <a-form-model :rules="rules" :model="content" ref="formMedia">
        <PrivilegesMedia
          ref="block-media"
          v-model="content"
          :selectedLanguage="selectedLanguage"
          :languages="languages"
          :sports="sports"
          :linkTemplates="linkTemplates"
          :formRef="$refs.formMedia"
          @selectLanguage="handleSelectLanguage"
          @openLinkModal="(linkObj) => openAddLinkTemplatesModal(linkObj)"
        />
      </a-form-model>
    </MainContainer>
    <a-modal v-model="addAppsModal" title="Add new apps" :footer="null" width="700px" :destroyOnClose="true">
      <AppsModal
        @selectApps="changeSelectedApps"
        @close="closeAddAppsModal"
        :modalAppsData="modalAppsData"
        :scroll="{ y: 450 }"
      />
    </a-modal>
    <a-modal v-model="addLinkTemplateModal" width="395px" :destroyOnClose="true" :footer="null">
      <template #title>
        <span class="modal-title">Add new template</span>
      </template>
      <TemplatesModal
        :templateLink="selectedRedirect?.link || ''"
        @addLink="handleAddTemplatesModal"
        @closeTemplateModal="() => addLinkTemplateModal = false"
      />
    </a-modal>
  </div>
</template>
<script>
import AppsModal from '@/components/custom/modals/appsModal.vue'
import TemplatesModal from '@/components/custom/modals/linkTemplates.vue'

import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'

import PrivilegesGeneral from '@/views/apps/privileges/form/general'
import PrivilegesMedia from '@/views/apps/privileges/form/media'

import { mapState } from 'vuex'

import apiClient from '@/services/axios'
import PrivilegeService from '@/services/api/apiPrivilegeService'
import ViewscreenService from '@/services/api/apiViewscreenService'
import { MEDIA_FORM_FIELDS } from '@/constants/privileges'

const initialForm = {
  privilegeName: '',
  isActive: true,
  periods: [],
  section: 'screens',
  privilegeViewscreens: [],
  privilegeScreens: [],
  privilegeSegments: [],
  privilegeApps: [],
}

export default {
  name: 'privilegeForm',
  components: {
    HeadSection,
    MainContainer,
    PrivilegesGeneral,
    PrivilegesMedia,
    AppsModal,
    TemplatesModal,
  },
  data() {
    return {
      privilegeToEdit: null,
      form: { ...initialForm },
      content: {
        ru: {
          ...MEDIA_FORM_FIELDS,
        },
      },
      loading: false,
      languages: [
        {
          id: 0,
          name: 'ru',
          desc: 'Russian',
        },
      ],
      screens: [
        { label: 'Bonuses and Privileges', value: 99 },
      ],
      sports: [],
      segments: [],
      linkTemplates: [],
      selectedLanguage: 'ru',
      addAppsModal: false,
      addLinkTemplateModal: false,
      modalAppsData: null,
      selectedRedirect: null,
      rules: {
        privilegeName: [
          {
            required: true,
            message: 'Privilege name is required',
            trigger: 'blur',
          },
        ],
        privilegeApps: [
          {
            type: 'array',
            required: true,
            message: 'Apps are required',
            trigger: 'change',
          },
        ],
        periods: [
          {
            type: 'array',
            required: true,
            message: 'Please enter at least one activity period',
            trigger: 'change',
          },
        ],
        privilegeScreens: [
          {
            required: true,
            message: 'Please select at least one screen',
            trigger: ['change', 'blur'],
          },
        ],
        privilegeSegments: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one segment',
            trigger: ['change', 'blur'],
          },
        ],
        sportId: [
          {
            required: true,
            message: 'SportId is required',
            trigger: 'blur',
            validator: (rule, value, callback) => {
              if (value) {
                callback()
              } else {
                callback(new Error(rule.message))
              }
            },
          },
        ],
      },
    }
  },
  computed: {
    ...mapState(['user']),
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isEditMode() {
      return this.$route.path.split('/').includes('edit')
    },
    currentMedia() {
      return this.content[this.selectedLanguage]
    },
    routeTitle() {
      return 'Privileges'
    },
    breadcrumbs() {
      return [
        { name: 'Privileges', link: '/privileges' },
        { name: this.isEditMode ? 'Edit privilege' : 'New privilege', link: undefined },
      ]
    },
    privilegeId() {
      if (!this.isEditMode) {
        return null
      }
      return this.$route.params.id
    },
  },
  methods: {
    validateFormField(fieldName) {
      this.$refs.form.validateField(fieldName)
    },
    updatePeriods(periods) {
      if (!periods?.length || !periods?.[0]?.starts_at || !periods?.[0]?.starts_at) {
        return
      }

      this.form.periods = JSON.parse(JSON.stringify(periods))
    },
    async deletePrivilege() {
      try {
        const response = await PrivilegeService.deletePrivilege(this.privilegeToEdit.uuid)
        if (response.data.data) {
          this.$notification.success({
            message: 'Privilege successfully deleted',
          })
          this.redirectToRoute()
        }
      } catch (err) {
        this.$notification.error({
          message: err.response.data.error.message,
        })
      }
    },
    async save() {
      const isValid = await this.validateForm()
      const isPeriodValid = await this.$refs['block-general']?.$refs?.['activity-periods']?.validate()
      const isFirstLinkValid = await this.$refs['block-media']?.$refs?.['link-1']?.validate() ?? true
      const isSecondLinkValid = await this.$refs['block-media']?.$refs?.['link-2']?.validate() ?? true

      if (!isValid || !isPeriodValid || !isFirstLinkValid || !isSecondLinkValid) {
        return
      }

      if (!this.isEditMode) {
        return this.createPrivilege()
      }

      this.updatePrivilege()
    },
    async validateForm() {
      const isGeneralFormValid = await new Promise((resolve) => {
        this.$refs.form.validate(resolve)
      })

      const isMediaFormValid = await new Promise((resolve) => {
        this.$refs.formMedia.validate(resolve)
      })

      return isGeneralFormValid && isMediaFormValid
    },
    clearForm() {
      this.form = { ...initialForm }
      this.initializeContent()
    },
    redirectToRoute() {
      // if (this.isViewscreen) {
      //   return this.redirectToViewscreen()
      // }
      return this.redirectToPrivileges()
    },
    redirectToPrivileges() {
      this.$router.push('/privileges')
    },
    async validateApps() {
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validateField('privilegeApps', resolve)
      })
      return isValid
    },
    async getPrivilege() {
      try {
        this.loading = true

        const response = await PrivilegeService.getPrivilege(this.$route.params.id)

        return response?.data?.data
      } catch (err) {
        this.$notification.error({
          message: err?.response?.data?.error?.message,
        })
      } finally {
        this.loading = false
      }
    },
    async createPrivilege() {
      try {
        this.loading = true

        const payload = this.getPayload()

        const response = await PrivilegeService.createPrivilege(payload)
        if (response?.data?.data) {
          this.$notification.success({
            message: 'Privilege successfully created',
          })
          this.redirectToRoute()
        }
      } catch (err) {
        this.$notification.error({
          message: err?.response?.data?.error?.message,
        })
      } finally {
        this.loading = false
      }
    },
    async updatePrivilege() {
      try {
        this.loading = true

        const payload = this.getPayload()

        const response = await PrivilegeService.updatePrivilege(this.privilegeToEdit.uuid, payload)

        if (response?.data?.data) {
          this.$notification.success({
            message: 'Privilege successfully updated',
          })

          this.redirectToRoute()
        }
      } catch (err) {
        this.$notification.error({
          message: err.response?.data?.error?.message,
        })
      } finally {
        this.loading = false
      }
    },
    getApplications() {
      return this.form.privilegeApps.map(app => ({
        application_id: app.application_id,
        min_app_version: app.min_app_version,
        max_app_version: app.max_app_version,
      }))
    },
    getMedia() {
      const initMediaObj = this.createMediaObj()

      return Object.entries(this.content).map(([key, value]) => {
        if (JSON.stringify(value) === JSON.stringify(initMediaObj)) {
          return null
        }

        const lid = this.languages.find(lang => lang.name === key)?.id ?? null

        const mediaObj = {
          lid,
          image_id: value.base.image.id,
          title: value.base.title,
          completion_date_display: value.base.showTime,
          button_is_active: value.base.hasButton,
        }

        if (value.base?.completionDate) {
          const format = value.base.showTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'
          mediaObj.completion_at = this.$moment(value.base.completionDate).tz('Etc/UTC').format(format)
        } else {
          mediaObj.completion_at = null
        }

        if (value.base.hasButton) {
          mediaObj.button_name = value.buttonProps?.buttonName
          mediaObj.button_type = value.buttonProps?.buttonType

          if (value.buttonProps.buttonType === 'dialogue') {
            mediaObj.dialogue_title = value.buttonProps?.dialogue?.title
            mediaObj.dialogue_text = value.buttonProps?.dialogue?.text
            mediaObj.dialogue_image_id = value.buttonProps?.dialogue?.image?.id

            if (value.buttonProps.dialogue.hasButton) {
              mediaObj.secondary_button_is_active = true
              mediaObj.secondary_button_type = 'link'
              mediaObj.secondary_button_link_type = value.buttonProps?.dialogue?.link?.type
              mediaObj.secondary_button_value = value.buttonProps?.dialogue?.link?.sportId?.toString() ?? value.buttonProps?.dialogue?.link?.link
              mediaObj.secondary_button_name = value.buttonProps?.dialogue?.buttonName
            }
          } else {
            mediaObj.button_link_type = value.buttonProps?.link?.type
            mediaObj.button_value = value.buttonProps?.link?.sportId?.toString() ?? value.buttonProps?.link?.link
          }
        }

        return mediaObj
      }).filter(Boolean)
    },
    getPayload() {
      const payload = {
        name: this.form.privilegeName,
        is_active: this.form.isActive,
        applications: this.getApplications(),
        segments: this.form.privilegeSegments,
        screens: this.form.privilegeScreens,
        langs: this.getMedia(),
        periods: this.$refs['block-general']?.$refs?.['activity-periods']?.getPeriods(),
      }

      return payload
    },
    openAddAppModal() {
      if (this.loading) {
        return
      }
      this.addAppsModal = true
    },
    closeAddAppsModal() {
      this.addAppsModal = false
    },
    changeSelectedApps(apps) {
      this.closeAddAppsModal()
      this.form.privilegeApps = apps
      this.modalAppsData = apps
      this.validateApps()
    },
    openAddLinkTemplatesModal(linkObj) {
      if (this.loading) {
        return
      }

      this.selectedRedirect = { ...linkObj }
      this.addLinkTemplateModal = true
    },
    async handleAddTemplatesModal(newTemplate) {
      this.addLinkTemplateModal = false
      this.linkTemplates.push(newTemplate)
    },
    async fetchSports() {
      this.localLoading = true
      const url = '/admin/sports?results=1000'
      try {
        const response = await apiClient.get(url)
        this.sports = response.data.data
      } catch (error) {
        console.log('Error while trying to get sports', error)
      } finally {
        this.localLoading = false
      }
    },
    async getLinkTemplates() {
      this.localLoading = true
      try {
        const url = '/admin/links?type=custom&results=1000'
        const response = await apiClient.get(url)
        if (response?.data?.data) {
          this.linkTemplates = JSON.parse(JSON.stringify(response.data.data))
        }
      } catch (error) {
        console.log('Error while trying to get templates', error)
      } finally {
        this.localLoading = false
      }
    },
    async getSegments() {
      this.localLoading = true
      try {
        const response = await ViewscreenService.getSegmentatorSegments()
        if (response.data.data) {
          this.segments = response.data.data
        }
      } finally {
        this.localLoading = false
      }
    },
    async fetchLocalizations() {
      this.languagesLoading = true
      const url = '/admin/localizations'
      try {
        const response = await apiClient.get(url)
        this.languages = response.data.data
      } catch (error) {
        console.log('Error while trying to get locales', error)
      } finally {
        this.languagesLoading = false
      }
    },
    handleSelectLanguage(lang) {
      this.selectedLanguage = lang
    },
    createMediaObj() {
      return JSON.parse(JSON.stringify(MEDIA_FORM_FIELDS))
    },
    initializeContent() {
      this.content = this.languages.reduce((acc, language) => {
        return {
          ...acc,
          [language.name]: JSON.parse(JSON.stringify(this.createMediaObj())),
        }
      }, {})
    },
    fillForm() {
      this.form.isActive = this.privilegeToEdit.is_active
      this.form.privilegeName = this.privilegeToEdit.name
      this.form.privilegeScreens = [...this.privilegeToEdit.screens]
      this.form.privilegeSegments = this.privilegeToEdit.segments?.map(s => s.id) ?? []
      this.form.privilegeApps = [...this.privilegeToEdit.applications]
      this.form.periods = [...this.privilegeToEdit.periods]

      const privilegeLanguages = this.privilegeToEdit.localizations?.map(loc => this.languages.find(l => l.id === loc.lid))
      this.selectedLanguage = privilegeLanguages[0].name

      const privilegeContent = privilegeLanguages.reduce((acc, language) => {
        const media = this.privilegeToEdit.localizations.find(loc => loc.lid === language.id)

        return {
          ...acc,
          [language.name]: {
            base: {
              image: {
                id: media.image?.id,
                url: media.image?.path,
                file: null,
                loading: false,
              },
              completionDate: media.completion_at ? this.$moment(media.completion_at).tz('Etc/GMT-3').format('YYYY-MM-DD HH:mm:ss') : null,
              showTime: media.completion_date_display ?? false,
              title: media.title,
              hasButton: media.button_is_active,
            },
            buttonProps: {
              buttonName: media.button_name,
              buttonType: media.button_type,
              link: {
                type: media.button_link_type,
                link: media.button_link_type === 'sport' ? null : media.button_value,
                sportId: media.button_link_type === 'sport' ? Number(media.button_value) : null,
              },
              dialogue: {
                title: media.dialogue_title,
                text: media.dialogue_text,
                hasButton: media.secondary_button_is_active,
                buttonName: media.secondary_button_name,
                image: {
                  id: media.dialogue_image?.id,
                  url: media.dialogue_image?.path,
                  file: null,
                  loading: false,
                },
                link: {
                  type: media.secondary_button_link_type,
                  link: media.secondary_button_link_type === 'sport' ? null : media.secondary_button_value,
                  sportId: media.secondary_button_link_type === 'sport' ? Number(media.secondary_button_value) : null,
                },
              },
            },
          },
        }
      }, {})

      const defaultContent = this.languages.reduce((acc, language) => {
        return {
          ...acc,
          [language.name]: this.createMediaObj(),
        }
      }, {})

      this.content = {
        ...defaultContent,
        ...privilegeContent,
      }
    },
  },
  async created() {
    this.fetchSports()
    this.getLinkTemplates()
    this.getSegments()
    await this.fetchLocalizations()

    if (this.isEditMode) {
      this.privilegeToEdit = await this.getPrivilege()
      this.fillForm()
    } else {
      this.selectedLanguage = this.languages[0].name
      this.initializeContent()
    }
  },
}
</script>
<style lang="scss">
.privilege-form {
  gap: 1rem;
  position: relative;

  .normal-size {
    max-width: 433px;
  }

  .bigger-size {
    max-width: 650px;
  }

  .ql-toolbar.ql-snow {
    line-height: 1.5;
  }

  .has-error {
    .ql-toolbar,
    .ql-container {
      border-color: #f5222e;
    }
  }

  .ant-tooltip-inner {
    font-weight: 400 !important;
    width: 217px;
    white-space: break-spaces;
    word-wrap: normal;
  }

  .switch label {
    margin: 0;
  }

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label {
    overflow: visible;
  }

  label i {
    margin-left: 4px;
  }

  .ant-input {
    height: 40px !important;
    border-radius: 2px !important;
    color: #595959 !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    font-size: 16px !important;
  }

  .ant-upload {
    margin: 0 !important;
  }

  .title {
    font-size: 14px;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: 0;
  }

  .add-link {
    color: #007bff;
    font-weight: 400;
  }

  .text-count {
    position: absolute;
    right: 0;
    font-size: 14px;
    line-height: 22px;
    color: #BFBFBF;
    text-align: end;
  }

  .required-label::before {
    display: inline-block;
    margin-right: 4px;
    color: #f5222e;
    font-size: 14px;
    font-family: SimSun, sans-serif;
    line-height: 1;
    content: '*';
  }
}
</style>
